import { firstName } from '../helpers';
import { useState } from 'react';
import { createPortal } from 'react-dom';

function ScalettaView({ event, tracks, musicians, canManage, orderingLocked, onToggleLock, onReorder, onToggleScaletta, onClose }) {
  const [localScaletta, setLocalScaletta] = useState(
    [...tracks].filter(t => t.in_scaletta).sort((a,b) => (a.position??999) - (b.position??999))
  );
  const [localPool, setLocalPool] = useState(
    [...tracks].filter(t => !t.in_scaletta)
  );
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const canDrag = !orderingLocked || canManage;

  const getVoice = t => {
    const pl = (t.track_players||[]).filter(p => p.role && p.role.toLowerCase().includes('voce'));
    return pl.map(p => {
      const m = musicians.find(mu => mu.id === p.musician_id);
      return m ? firstName(m.name) : '?';
    }).join(', ');
  };

  // ── Drag and drop (solo nella sezione scaletta) ───────────────────────────
  const onDragStart = (e, i) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(i));
  };
  const onDragOver = (e, i) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (i !== dragOverIdx) setDragOverIdx(i);
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const moveTrack = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= localScaletta.length) return;
    const newList = [...localScaletta];
    const [moved] = newList.splice(fromIdx, 1);
    newList.splice(toIdx, 0, moved);
    setLocalScaletta(newList);
    setSaving(true);
    try { await Promise.all(newList.map((t, idx) => onReorder(t.id, idx))); }
    finally { setSaving(false); }
  };

  const onDrop = async (e, i) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData('text/plain'));
    if (isNaN(from) || from === i) { setDragIdx(null); setDragOverIdx(null); return; }
    const newList = [...localScaletta];
    const [moved] = newList.splice(from, 1);
    newList.splice(i, 0, moved);
    setLocalScaletta(newList);
    setDragIdx(null); setDragOverIdx(null);
    setSaving(true);
    try { await Promise.all(newList.map((t, idx) => onReorder(t.id, idx))); }
    finally { setSaving(false); }
  };

  // ── Aggiungi / rimuovi dalla scaletta ─────────────────────────────────────
  const handleAdd = async (track) => {
    const updated = { ...track, in_scaletta: true };
    setLocalPool(prev => prev.filter(t => t.id !== track.id));
    setLocalScaletta(prev => [...prev, updated]);
    await onToggleScaletta(track.id, true);
  };

  const handleRemove = async (track) => {
    const updated = { ...track, in_scaletta: false };
    setLocalScaletta(prev => prev.filter(t => t.id !== track.id));
    setLocalPool(prev => [...prev, updated]);
    await onToggleScaletta(track.id, false);
  };

  // Data per stampa
  const eventDate = event.date ? new Date(event.date).toLocaleDateString('it-IT', {day:'numeric',month:'long',year:'numeric'}) : '';
  const venueLine = [eventDate, event.location_name||event.location||'', event.city, event.time_start ? `ore ${event.time_start}` : ''].filter(Boolean).join(' · ');

  return createPortal(
    <div id="scaletta-portal" style={{
      position:'fixed', top:0, left:0, right:0, bottom:0,
      zIndex:9999, background:'#181614',
      display:'flex', flexDirection:'column',
    }}>

      {/* ── Print styles ─────────────────────────────────────────── */}
      <style>{`
        @media print {
          body > *:not(#scaletta-portal) { display: none !important; }
          #scaletta-portal { position: static !important; background: #fff !important; display: block !important; }
          .screen-only { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 8mm 10mm; size: A4 portrait; }
          #scaletta-portal .print-only { padding: 0 !important; }
          #scaletta-portal .print-only table td,
          #scaletta-portal .print-only table th { padding-top: 3px !important; padding-bottom: 3px !important; }
          #scaletta-portal .print-only table td div:first-child { font-size: 11px !important; }
          #scaletta-portal .print-only table td div:last-child { font-size: 9px !important; margin-top: 0 !important; }
          #scaletta-portal .print-only table td:first-child { font-size: 9px !important; }
          #scaletta-portal .print-only table td:nth-child(3),
          #scaletta-portal .print-only table td:nth-child(4) { font-size: 10px !important; }
          #scaletta-portal .print-only > div:first-child { margin-bottom: 6px !important; padding-bottom: 6px !important; }
          #scaletta-portal .print-only > div:first-child div:first-child { font-size: 16px !important; }
          #scaletta-portal .print-only > div:first-child div:last-child { font-size: 10px !important; }
          #scaletta-portal .print-only > div:nth-child(2) { margin: 6px 0 10px !important; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>

      {/* ── Print-only content (solo brani in scaletta) ──────────── */}
      <div className="print-only" style={{
        background:'#fff', padding:'16px 20px',
        fontFamily:'Arial,sans-serif', color:'#000',
      }}>
        <div style={{fontSize:28,fontWeight:700,color:'#000'}}>{event.title}</div>
        <div style={{fontSize:18,color:'#555',marginTop:4}}>{venueLine}</div>
        <div style={{height:3,background:'#ED4D1D',margin:'16px 0 20px'}}/>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{borderBottom:'1.5px solid #000'}}>
              <th style={{textAlign:'left',padding:'0 10px 8px 0',fontSize:15,letterSpacing:'1.5px',color:'#777',width:32}}>#</th>
              <th style={{textAlign:'left',padding:'0 10px 8px',fontSize:15,letterSpacing:'1.5px',color:'#777'}}>BRANO</th>
              <th style={{textAlign:'left',padding:'0 10px 8px',fontSize:15,letterSpacing:'1.5px',color:'#777',width:80}}>TON.</th>
              <th style={{textAlign:'left',padding:'0 0 8px 10px',fontSize:15,letterSpacing:'1.5px',color:'#777',width:130}}>VOCE</th>
            </tr>
          </thead>
          <tbody>
            {localScaletta.map((t, i) => {
              const voice = getVoice(t);
              return (
                <tr key={t.id} style={{borderBottom:'1px solid #f0f0f0', background:i%2===0?'#fff':'#fafafa', pageBreakInside:'avoid'}}>
                  <td style={{padding:'7px 6px 7px 0',color:'#ED4D1D',fontWeight:700,fontSize:17}}>{String(i+1).padStart(2,'0')}</td>
                  <td style={{padding:'7px 8px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    <span style={{fontSize:19,fontWeight:700,color:'#000'}}>{t.title}</span>
                    {t.artist && <span style={{fontSize:16,color:'#777',marginLeft:6}}>{t.artist}</span>}
                  </td>
                  {/* La tonalita' si legge sul leggio un attimo prima di attaccare:
                      e' l'informazione piu' cercata di tutto il foglio. */}
                  <td style={{padding:'7px 8px',fontSize:19,fontWeight:700,color:'#000'}}>{t.key||'—'}</td>
                  <td style={{padding:'7px 0 7px 8px',fontSize:18,fontWeight:voice?700:400,color:voice?'#ED4D1D':'#bbb'}}>{voice||'—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{marginTop:20,paddingTop:10,borderTop:'1px solid #eee',fontSize:15,color:'#aaa',display:'flex',justifyContent:'space-between'}}>
          <span>Jammo · jammo.live</span>
          <span>{localScaletta.length} brani</span>
        </div>
      </div>

      {/* ── Screen UI ────────────────────────────────────────────── */}
      <div className="screen-only" style={{display:'flex',flexDirection:'column',height:'100%'}}>

        {/* Header */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          paddingTop:'max(env(safe-area-inset-top), 14px)',
          padding:'max(env(safe-area-inset-top), 14px) 16px 10px',
          background:'#1F1C18',
          borderBottom:'1px solid rgba(237,77,29,0.15)',
          flexShrink:0,
        }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Chiudi</button>
          <div style={{fontSize:17,fontWeight:800,color:'#E8DDD0',flex:1,textAlign:'center',padding:'0 12px',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{event.title}</div>
          <div style={{display:'flex',gap:8,flexShrink:0}}>
            {canManage && (
              <button className="btn btn-secondary btn-sm" onClick={onToggleLock}>
                {orderingLocked ? 'Bloccata' : 'Libera'}
              </button>
            )}
          </div>
        </div>

        {/* Hint */}
        {canDrag && (
          <div style={{padding:'7px 16px',fontSize:16,color:'#8A8377',background:'#1F1C18',flexShrink:0,textAlign:'center',borderBottom:'1px solid rgba(237,77,29,0.06)'}}>
            {saving ? 'Salvataggio...' : 'Trascina per riordinare la scaletta'}
          </div>
        )}

        {/* Lista */}
        <div style={{flex:1,overflowY:'auto',padding:'0 16px 24px'}}>

          {/* ── Sezione scaletta ─────────────────────────────────── */}
          <div style={{padding:'16px 0 10px',borderBottom:'2px solid #ED4D1D',marginBottom:8}}>
            <div style={{fontSize:26,fontWeight:800,color:'#E8DDD0'}}>Scaletta</div>
            <div style={{fontSize:16,color:'#8A8377',marginTop:3}}>{localScaletta.length} brani programmati</div>
          </div>

          {localScaletta.length === 0 && (
            <div style={{textAlign:'center',padding:'20px 0',color:'#706A60',fontSize:16}}>
              Nessun brano in scaletta. Aggiungine dal pool qui sotto.
            </div>
          )}

          {/* Header colonne scaletta */}
          {localScaletta.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'30px 1fr 58px 88px 40px',padding:'4px 0 6px',borderBottom:'1px solid rgba(237,77,29,0.2)',marginBottom:2}}>
              {['#','BRANO','TON.','VOCE',''].map(h=>(
                <div key={h} style={{fontSize:15,fontWeight:700,color:'#8A8377',letterSpacing:'1px',padding:'0 4px'}}>{h}</div>
              ))}
            </div>
          )}

          {localScaletta.map((t, i) => {
            const voice = getVoice(t);
            return (
              <div key={t.id}
                draggable={canDrag}
                onDragStart={e=>onDragStart(e,i)}
                onDragOver={e=>onDragOver(e,i)}
                onDrop={e=>onDrop(e,i)}
                onDragEnd={onDragEnd}
                style={{
                  display:'grid', gridTemplateColumns:'30px 1fr 58px 88px 40px',
                  alignItems:'center', padding:'8px 0',
                  borderBottom: dragOverIdx===i&&dragIdx!==i ? '2px solid #ED4D1D' : '1px solid rgba(237,77,29,0.07)',
                  opacity: dragIdx===i ? 0.3 : 1,
                  cursor: canDrag ? 'grab' : 'default',
                  transition:'opacity .1s', userSelect:'none',
                }}>
                <div style={{padding:'0 4px',display:'flex',flexDirection:'column',alignItems:'center',gap:2,flexShrink:0}}>
                  <span style={{fontSize:16,fontWeight:700,color:'#ED4D1D',fontFamily:'monospace'}}>{String(i+1).padStart(2,'0')}</span>
                  {/* Le frecce si premono al buio e di fretta: il bersaglio e'
                      tutta la larghezza della colonna, non solo il segno. */}
                  {canDrag && <div style={{display:'flex',flexDirection:'column',gap:0}}>
                    <button onClick={()=>moveTrack(i,i-1)} disabled={i===0}
                      aria-label="Sposta su"
                      style={{background:'none',border:'none',cursor:i===0?'default':'pointer',
                        color:i===0?'#2A2520':'#ED4D1D',fontSize:17,width:30,height:24,padding:0,lineHeight:1}}>▲</button>
                    <button onClick={()=>moveTrack(i,i+1)} disabled={i===localScaletta.length-1}
                      aria-label="Sposta giu"
                      style={{background:'none',border:'none',cursor:i===localScaletta.length-1?'default':'pointer',
                        color:i===localScaletta.length-1?'#2A2520':'#ED4D1D',fontSize:17,width:30,height:24,padding:0,lineHeight:1}}>▼</button>
                  </div>}
                </div>
                <div style={{padding:'0 8px'}}>
                  <div style={{fontSize:20,fontWeight:800,color:'#E8DDD0',lineHeight:1.25}}>{t.title}</div>
                  {t.artist && <div style={{fontSize:16,color:'#B5AA9A',marginTop:2}}>{t.artist}</div>}
                </div>
                <div style={{padding:'0 4px',fontSize:18,fontWeight:700,color:'#E8DDD0'}}>{t.key||'—'}</div>
                <div style={{padding:'0 4px',fontSize:17,fontWeight:voice?600:400,color:voice?'var(--text)':'#4A4540'}}>{voice||'—'}</div>
                <div style={{padding:'0 4px',display:'flex',justifyContent:'center'}}>
                  <button
                    onClick={()=>handleRemove(t)}
                    title="Rimuovi dalla scaletta"
                    style={{
                      width:36, height:36, borderRadius:8, border:'1px solid rgba(255,255,255,0.1)',
                      background:'rgba(255,255,255,0.04)', color:'#8A8377',
                      cursor:'pointer', fontSize:22, lineHeight:1,
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                    −
                  </button>
                </div>
              </div>
            );
          })}

          {/* ── Sezione pool ─────────────────────────────────────── */}
          <div style={{padding:'24px 0 10px',borderBottom:'1px solid rgba(255,255,255,0.08)',marginBottom:8,marginTop:8}}>
            <div style={{fontSize:22,fontWeight:800,color:'#B5AA9A'}}>Pool</div>
            <div style={{fontSize:16,color:'#8A8377',marginTop:3}}>{localPool.length} brani disponibili</div>
          </div>

          {localPool.length === 0 && (
            <div style={{textAlign:'center',padding:'16px 0',color:'#706A60',fontSize:16}}>
              Tutti i brani sono in scaletta.
            </div>
          )}

          {localPool.map(t => (
            <div key={t.id} style={{
              display:'flex', alignItems:'center', gap:8, padding:'8px 0',
              borderBottom:'1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:19,fontWeight:600,color:'#B5AA9A',lineHeight:1.25,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{t.title}</div>
                {t.artist && <div style={{fontSize:16,color:'#8A8377',marginTop:2}}>{t.artist}</div>}
              </div>
              <button
                onClick={()=>handleAdd(t)}
                title="Aggiungi alla scaletta"
                style={{
                  width:36, height:36, borderRadius:8, border:'1px solid rgba(237,77,29,0.3)',
                  background:'rgba(237,77,29,0.08)', color:'#ED4D1D',
                  cursor:'pointer', fontSize:24, lineHeight:1, flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                +
              </button>
            </div>
          ))}
        </div>

        {/* Footer stampa */}
        <div style={{
          padding:'12px 16px',
          paddingBottom:'max(env(safe-area-inset-bottom), 16px)',
          background:'#1F1C18',
          borderTop:'1px solid rgba(237,77,29,0.12)',
          flexShrink:0,
        }}>
          <button className="btn btn-secondary" style={{width:'100%',justifyContent:'center'}}
            onClick={()=>window.print()}>
            Stampa / Salva PDF
          </button>
        </div>
      </div>

    </div>,
    document.body
  );
}

export default ScalettaView;
